import React, { useEffect, useState } from "react";
import { Share, Plus, Trash, Copy, X, Check, Eye, ShieldCheck } from "@phosphor-icons/react";
import { listShareLinks, createShareLink, revokeShareLink } from "@/lib/api";

export default function ShareLinksView() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [label, setLabel] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState("");
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    setLinks(await listShareLinks());
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function handleCreate() {
    if (!label.trim()) { setErr("Label is required."); return; }
    setBusy(true); setErr("");
    try {
      const exp = expiresAt ? new Date(expiresAt).toISOString() : "";
      const link = await createShareLink({ label: label.trim(), expiresAt: exp });
      setReport(`Created share link "${link.label}". Copy the URL below to share.`);
      setLabel(""); setExpiresAt("");
      setShowForm(false);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  async function handleRevoke(link) {
    if (!window.confirm(`Revoke share link "${link.label}"? Anyone with the URL will lose access immediately.`)) return;
    await revokeShareLink(link.id);
    setReport(`Revoked "${link.label}".`);
    await load();
  }

  function shareUrl(token) {
    return `${window.location.origin}/share/${token}`;
  }

  async function copy(token) {
    try { await navigator.clipboard.writeText(shareUrl(token)); setReport("Link copied to clipboard."); }
    catch (e) { setReport(`Copy failed: ${e.message}`); }
  }

  return (
    <div className="view-grid" data-testid="share-links-view">
      <div className="panel">
        <div className="panel-header">
          <Share size={18} weight="bold" />
          <span>SHARE LINKS</span>
          <span className="header-count mono">{links.length}</span>
          <button className="btn btn--accent btn--sm ml-auto" onClick={() => setShowForm(true)} data-testid="add-share-btn">
            <Plus size={12} weight="bold" /> NEW SHARE LINK
          </button>
        </div>
        <div className="panel-body">
          <p className="panel-desc">
            Generate a read-only URL to share your claim overview with a VSO rep, family member, or attorney. They'll see your conditions, evidence, appointments, prescriptions, and statements — but NOT billing details, admin info, or your account email. Revoke any link instantly.
          </p>
          <div className="share-privacy-note">
            <ShieldCheck size={14} weight="bold" />
            <span>Shared data is read-only. No login required for the viewer. You can revoke access at any time.</span>
          </div>
          {report && <div className="form-report" data-testid="share-report">{report}</div>}
        </div>
      </div>

      <div className="panel">
        <div className="panel-body" style={{ padding: 0 }}>
          {loading && <div className="admin-empty">Loading...</div>}
          {!loading && links.length === 0 && (
            <div className="admin-empty" data-testid="share-empty">
              No share links yet. Click NEW SHARE LINK to create one.
            </div>
          )}
          {!loading && links.length > 0 && (
            <div className="table-scroll">
              <div className="table-scroll-hint">← scroll →</div>
            <table className="admin-table table--cards-on-mobile" data-testid="share-table">
              <thead>
                <tr>
                  <th>LABEL</th>
                  <th>CREATED</th>
                  <th>EXPIRES</th>
                  <th className="mono">VIEWS</th>
                  <th>LAST VIEWED</th>
                  <th>URL</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {links.map((link) => (
                  <tr key={link.id} data-testid={`share-row-${link.id}`}>
                    <td data-label="LABEL" style={{ fontWeight: 700 }}>{link.label}</td>
                    <td data-label="CREATED" className="mono small">{(link.createdAt || "").slice(0, 10)}</td>
                    <td data-label="EXPIRES" className="mono small">{link.expiresAt ? link.expiresAt.slice(0, 10) : "—"}</td>
                    <td data-label="VIEWS" className="mono">{link.accessCount || 0}</td>
                    <td data-label="LAST VIEWED" className="mono small">{link.lastAccessedAt ? link.lastAccessedAt.slice(0, 10) : "—"}</td>
                    <td data-label="URL" className="mono small invite-link-cell">
                      <code className="invite-link-code">/share/{link.token.slice(0, 10)}…</code>
                    </td>
                    <td data-label="ACTIONS">
                      <div className="row-actions">
                        <button className="btn btn--outline btn--sm" onClick={() => copy(link.token)} data-testid={`share-copy-${link.id}`}>
                          <Copy size={11} weight="bold" /> COPY
                        </button>
                        <a className="btn btn--outline btn--sm" href={shareUrl(link.token)} target="_blank" rel="noopener noreferrer" data-testid={`share-open-${link.id}`}>
                          <Eye size={11} weight="bold" /> OPEN
                        </a>
                        <button className="btn btn--danger btn--sm" onClick={() => handleRevoke(link)} data-testid={`share-revoke-${link.id}`}>
                          <Trash size={11} weight="bold" /> REVOKE
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="modal-backdrop" data-testid="share-form-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className="modal-panel">
            <div className="modal-header">
              <Share size={16} weight="bold" />
              <span>NEW SHARE LINK</span>
              <button className="modal-close" onClick={() => setShowForm(false)}><X size={14} weight="bold" /></button>
            </div>
            <div className="modal-body">
              <label className="field">
                <span className="field-label">LABEL *</span>
                <input className="field-input" placeholder="e.g., For my VSO, Mom, Attorney" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="share-label-input" />
                <span className="field-hint">Only you see this label — helps you remember who you gave the link to.</span>
              </label>
              <label className="field">
                <span className="field-label">EXPIRES ON (OPTIONAL)</span>
                <input type="date" className="field-input" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} data-testid="share-expires-input" />
                <span className="field-hint">Leave blank for "never expires" — you can still revoke manually.</span>
              </label>
              {err && <div className="form-report form-report--err" data-testid="share-error">{err}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn--outline" onClick={() => setShowForm(false)} disabled={busy}>CANCEL</button>
              <button className="btn btn--accent" onClick={handleCreate} disabled={busy} data-testid="share-create-btn">
                <Check size={14} weight="bold" /> {busy ? "Creating..." : "CREATE LINK"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
